import { useAuth } from '@/hooks/useAuth';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

type AppRole = 'admin' | 'staff' | 'instructor' | 'parent';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: AppRole[];
}

export const ProtectedRoute = ({ children, requiredRoles }: ProtectedRouteProps) => {
  const { user, loading, isAdmin, isStaff, isInstructor, isParent } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    // Pass the current path as returnTo so user comes back after login
    const returnTo = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?returnTo=${returnTo}`} replace />;
  }

  // If specific roles are required, check if user has any of them
  if (requiredRoles && requiredRoles.length > 0) {
    const roleMap: Record<AppRole, boolean> = {
      admin: isAdmin,
      staff: isStaff,
      instructor: isInstructor,
      parent: isParent,
    };

    const hasRequiredRole = requiredRoles.some(role => roleMap[role]);

    if (!hasRequiredRole) {
      // Redirect to dashboard if user doesn't have required role
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};
